/**
 * MediaPicker - A reusable component for selecting media items
 */

import { useState } from "react";
import { useAuth0 } from "@auth0/auth0-react";
import { useQuery } from "@tanstack/react-query";
import { getGlobalMediaLibrary, MediaItem } from "../../api/media";
import { useMediaUrl } from "../../hooks/useMediaUrl";

interface MediaPickerProps {
  selectedMediaIds: number[];
  onAddMedia: (mediaId: number) => void;
  onRemoveMedia: (mediaId: number) => void;
  placeholder?: string;
  className?: string;
}

// Component to display a media thumbnail
function MediaThumbnail({ mediaId, filename }: { mediaId: number; filename: string }) {
  const { url, isLoading } = useMediaUrl(mediaId);
  
  if (isLoading) {
    return (
      <div className="h-20 w-20 bg-vault-100 rounded animate-pulse" />
    );
  }
  
  return (
    <img
      src={url || ""}
      alt={filename}
      className="h-20 w-20 object-cover rounded"
    />
  );
}

export default function MediaPicker({
  selectedMediaIds = [],
  onAddMedia,
  onRemoveMedia,
  placeholder = "Search for media to add...",
  className = "",
}: MediaPickerProps) {
  const { getAccessTokenSilently } = useAuth0();

  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState("");

  // Fetch media
  const { data: mediaData, isLoading } = useQuery({
    queryKey: ["media-picker", search],
    queryFn: async () => {
      const token = await getAccessTokenSilently();
      return getGlobalMediaLibrary(token, { 
        search: search || undefined, 
        pageSize: 50 
      });
    },
  });

  const mediaItems = mediaData?.items || [];

  // Check if a media item is selected
  const isSelected = (mediaId: number) => selectedMediaIds.includes(mediaId);

  const handleSelect = (media: MediaItem) => {
    if (isSelected(media.id)) {
      onRemoveMedia(media.id);
    } else {
      onAddMedia(media.id);
    }
  };

  return (
    <div className={`relative ${className}`}>
      {/* Trigger */}
      <div
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center justify-between w-full rounded border border-vault-200 px-3 py-2 bg-white cursor-pointer hover:border-vault-300"
      >
        <div className="flex items-center gap-2 min-w-0">
          {selectedMediaIds.length > 0 ? (
            <span className="text-vault-900">
              {selectedMediaIds.length} media item{selectedMediaIds.length !== 1 ? 's' : ''} selected
            </span>
          ) : (
            <span className="text-vault-400">{placeholder}</span>
          )}
        </div>
        <svg xmlns="http://www.w3.org/2000/svg" className={`h-4 w-4 text-vault-400 transition-transform ${isOpen ? "rotate-180" : ""}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </div>

      {/* Dropdown */}
      {isOpen && (
        <div className="absolute z-50 mt-1 w-full rounded-lg border border-vault-200 bg-white shadow-lg">
          {/* Search input */}
          <div className="p-2 border-b border-vault-100">
            <input
              type="text"
              placeholder="Search media by filename or description..."
              className="w-full rounded border border-vault-200 px-3 py-1.5 text-sm"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              autoFocus
            />
          </div>

          {/* Media grid */}
          <div className="max-h-96 overflow-y-auto p-3">
            {isLoading ? (
              <div className="p-3 text-center text-vault-500 text-sm">Loading...</div>
            ) : mediaItems.length > 0 ? (
              <div className="grid grid-cols-4 gap-3">
                {mediaItems.map((media) => (
                  <div
                    key={media.id}
                    onClick={() => handleSelect(media)}
                    className={`group relative cursor-pointer rounded border-2 transition-all ${
                      isSelected(media.id)
                        ? "border-vault-600 bg-vault-50"
                        : "border-transparent hover:border-vault-300"
                    }`}
                  >
                    <MediaThumbnail mediaId={media.id} filename={media.filename} />
                    
                    {/* Selection indicator */}
                    {isSelected(media.id) && (
                      <div className="absolute top-1 right-1 h-5 w-5 flex items-center justify-center rounded-full bg-vault-600 text-white">
                        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                        </svg>
                      </div>
                    )}
                    
                    {/* Filename tooltip */}
                    <div className="absolute bottom-0 left-0 right-0 bg-black/70 text-white text-xs px-1 py-0.5 truncate rounded-b opacity-0 group-hover:opacity-100 transition-opacity">
                      {media.original_filename || media.filename}
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div className="p-3 text-center text-vault-500 text-sm">
                {search ? "No media found" : "No media available"}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Click outside to close */}
      {isOpen && (
        <div
          className="fixed inset-0 z-40"
          onClick={() => setIsOpen(false)}
        />
      )}
    </div>
  );
}
